import { FEATURE_FLAG_KEYS } from '@alpha-core/feature-flags';
import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FeatureFlagsService } from '@neutrinos/ngx-feature-flags';
import { errorStyle, successStyle, warningStyle } from '../utils/console-styles';
import { InjectFFIntoWindow } from '../utils/inject-ff-into-window';
import { getPluginTimeoutMs, listenerPromise } from '../utils/listener-promise';
import { PackageInitializerUtil, PackageType } from '../utils/package-initializer.util';
import { BranchStateService } from './branch-state.service';
import { ProjectService } from './project.service';
import { UserService } from './user-service';

interface CustomWindow extends Window {
    customCodePlugin?: Record<string, any>;
}
declare let window: CustomWindow;
let pluginsRegisteredWhenProjectPluginsFeatureIsOff = false;

@Injectable({ providedIn: 'root' })
export class ProjectInitializerService {
    projectService = inject(ProjectService);
    userService = inject(UserService);
    router = inject(Router);
    route = inject(ActivatedRoute);
    featureFlagService = inject(FeatureFlagsService);
    projectId: string | null = null;
    packageInitUtil!: PackageInitializerUtil;

    initialized: boolean = false;

    getProjectIdFromQuery(): string | null {
        const queryParams = new URLSearchParams(this.router.url.split('?')[1] || '');
        return queryParams.get('projectId');
    }

    async init(
        projectId: any,
        loadElements: boolean = true,
        adminBundlesScriptBasePath: string = '',
        adminConfigServiceUrl: string = '',
    ): Promise<void> {
        try {
            InjectFFIntoWindow(this.featureFlagService);
            const project = await this.projectService.getProject(projectId);
            if (!project || !project.projectId) {
                this.router.navigate(['404']);
                return;
            }

            if (this.initialized) return;
            const tenant = this.userService?.getStoredUserInfo()?.userInfo?.organisationId || 'neutrinos';

            this.packageInitUtil = new PackageInitializerUtil(
                project.projectId,
                tenant,
                (project as any)?.currentTag?.name || '1.0.0',
                loadElements,
            );
            //check if project plugin feature is disabled then just call requestForBundles and requestForelemenet- if loadElements is true
            if (!this.featureFlagService.isFeatureEnabled(FEATURE_FLAG_KEYS.PROJECT_PLUGINS)) {
                if (!pluginsRegisteredWhenProjectPluginsFeatureIsOff) {
                    this.packageInitUtil.requestForBundles(adminBundlesScriptBasePath);
                    await listenerPromise(
                        'alpha_ready',
                        '[Alpha-Module-Manager] Successfully Injected',
                        getPluginTimeoutMs(),
                    );
                    if (loadElements) {
                        await this.packageInitUtil.requestForElements();
                    }
                    pluginsRegisteredWhenProjectPluginsFeatureIsOff = true;
                }
                return;
            }
            const { projectPlugins, customCodePlugins } = await this.projectService.fetchProjectPluginDependency(
                project,
                tenant,
            );

            const globalCustomCodeBundleUrl = this.getPathforCustomCodeBundle(
                project.projectId,
                (project as any).currentTag?.name || '',
                adminConfigServiceUrl,
            );

            const promises: Promise<any>[] = [];

            const alphaPluginPromise = (async () => {
                this.packageInitUtil.loadAlphaModules(false, adminBundlesScriptBasePath);
                await listenerPromise(
                    'alpha_ready',
                    '[Alpha-Module-Manager] Successfully Injected',
                    getPluginTimeoutMs(),
                );

                this.packageInitUtil.fetchBundleAndInjectScript(projectPlugins, PackageType.MODULE, 'ap');
                await this.bundleListenerPromise('alpha-ap-loaded', this.packageInitUtil, loadElements);
            })();
            promises.push(alphaPluginPromise);

            const customCodePluginPromise = (async () => {
                if (customCodePlugins.length > 0) {
                    this.packageInitUtil.fetchBundleAndInjectScript(
                        customCodePlugins,
                        PackageType.MODULE,
                        'customCode',
                    );
                    this.bundleListenerPromise('alpha-customCodePlugin-loaded', this.packageInitUtil, loadElements);
                }

                this.packageInitUtil.injectScriptFromUrl({ url: globalCustomCodeBundleUrl, integrity: '' });
                await this.bundleListenerPromise('global-custom-codes-loaded', this.packageInitUtil, loadElements);
            })();
            promises.push(customCodePluginPromise);

            await Promise.all(promises);
            this.initialized = true;
        } catch (error: any) {
            let message = '';
            if (error?.injectPlugins) {
                message =
                    error?.injectPlugins == true
                        ? '[Alpha-Modules] Failed to Inject'
                        : '[Alpha-Module-Manager] Failed to Inject';
            } else message = error?.message;

            console.log(`%c✖️${message}`, errorStyle);
            throw error;
        }
    }

    getPathforCustomCodeBundle(projectId: string, tag: string, adminConfigServiceUrl: string) {
        let path = `/pages/code-bundle/${projectId}`;
        const params = new URLSearchParams();
        if (tag.length) {
            params.set('tag', tag);
        }
        const branch = BranchStateService.getBranchFromUrl();
        params.set('branch', branch);
        return (
            (adminConfigServiceUrl || window.environments['ALPHA_UI_CONFIG_SERVICE_URL']) +
            path +
            '?' +
            params.toString()
        );
    }

    bundleListenerPromise = (event: string, packageInitUtil: PackageInitializerUtil, loadElements: boolean = true) => {
        return new Promise<void>((resolve, reject) => {
            const listener = async (e: Event) => {
                const customEvent = e as CustomEvent;
                const { success, message, errorKey } = customEvent.detail || {};
                window.removeEventListener(event, listener);

                if (!success) {
                    if (event == 'alpha-customCodePlugin-loaded' && errorKey == 'NO_PACKAGES_FOUND') {
                        console.log(`%c⚠️${message}`, warningStyle);
                        window.customCodePlugin = {};
                        resolve();
                    } else reject(new Error(message));
                } else {
                    if (event == 'alpha-ap-loaded') {
                        try {
                            if (loadElements) {
                                await packageInitUtil.requestForElements();
                            }
                        } catch (error) {
                            reject(error);
                        }
                    }
                    console.log(`%c✔️${message}`, successStyle);
                    resolve();
                }
            };
            window.addEventListener(event, listener);
        });
    };
}
